import Phaser from 'phaser';
import { LEVEL_COUNT, LEVELS } from '../../content/levels';
import { COLORS, drawLevelBackground, drawPlatform } from '../art';
import {
  applyDamage,
  BOSS_STAGGER_HITS,
  BOSS_TRAP_MS,
  BUBBLE_COOLDOWN_MS,
  bubblePhase,
  canRescue,
  madeHorizontalProgress,
  MAX_PLAYER_BUBBLES,
  registerBossBubbleHit,
  safeHorizontalDirection,
  ScoreChain,
} from '../logic';
import type {
  EnemyBehavior,
  EnemyKind,
  EnemySpawn,
  LevelDefinition,
  Point,
  RunState,
  WindZone,
} from '../types';
import { audio } from '../systems/AudioManager';
import { InputRouter } from '../systems/InputRouter';
import { ProgressStore } from '../systems/ProgressStore';
import { configureLogicalCamera, LOGICAL_HEIGHT, LOGICAL_WIDTH } from '../systems/DisplayManager';
import { titleStyle } from './MenuScene';

type GameInit = RunState;

interface PlayerRuntime {
  slot: 0 | 1;
  sprite: Phaser.Physics.Arcade.Sprite;
  hearts: number;
  active: boolean;
  rescueUsed: boolean;
  rescueBubble?: Phaser.Physics.Arcade.Sprite;
  rescueStartedAt: number;
  invulnerableUntil: number;
  lastBubbleAt: number;
  facing: -1 | 1;
  poseUntil: number;
}

interface EnemyRuntime {
  sprite: Phaser.Physics.Arcade.Sprite;
  kind: EnemyKind;
  behavior: EnemyBehavior;
  direction: -1 | 1;
  baseSpeed: number;
  alive: boolean;
  trapped: boolean;
  angry: boolean;
  nextJumpAt: number;
  originY: number;
  phase: number;
  action: 'roam' | 'warn' | 'charge';
  actionUntil: number;
  nextActionAt: number;
  roamTargetX: number;
  nextRetargetAt: number;
  lastProgressX: number;
  lastProgressAt: number;
  navigationLockUntil: number;
  recoveries: number;
}

interface BubbleRuntime {
  sprite: Phaser.Physics.Arcade.Sprite;
  owner: 0 | 1;
  direction: -1 | 1;
  createdAt: number;
  trappedAt?: number;
  enemy?: EnemyRuntime;
  popAllowedAt: number;
}

interface BossRuntime {
  sprite: Phaser.Physics.Arcade.Sprite;
  name: string;
  health: number;
  maxHealth: number;
  direction: -1 | 1;
  nextJumpAt: number;
  bubbleHits: number;
  trapped: boolean;
  trapBubble?: Phaser.Physics.Arcade.Sprite;
  trappedAt: number;
  popAllowedAt: number;
  relocationIndex: number;
  relocationPoints: Point[];
  stunnedUntil: number;
}

interface EnemyProjectile {
  sprite: Phaser.Physics.Arcade.Sprite;
  createdAt: number;
}

export class GameScene extends Phaser.Scene {
  private run!: GameInit;
  private level!: LevelDefinition;
  private inputRouter!: InputRouter;
  private scoreChain!: ScoreChain;
  private platforms!: Phaser.Physics.Arcade.StaticGroup;
  private players: PlayerRuntime[] = [];
  private enemies: EnemyRuntime[] = [];
  private bubbles: BubbleRuntime[] = [];
  private enemyProjectiles: EnemyProjectile[] = [];
  private boss?: BossRuntime;
  private hudScore!: Phaser.GameObjects.Text;
  private hudHearts!: Phaser.GameObjects.Text;
  private hudMultiplier!: Phaser.GameObjects.Text;
  private bossHud?: Phaser.GameObjects.Text;
  private pauseOverlay?: Phaser.GameObjects.Container;
  private isPaused = false;
  private completing = false;
  private stageStartedAt = 0;
  private playableAt = 0;
  private blurHandler = () => this.pauseGame(true);
  private store = new ProgressStore();

  constructor() {
    super('Game');
  }

  init(data: GameInit): void {
    this.run = {
      mode: data.mode ?? 'solo',
      levelIndex: Phaser.Math.Clamp(data.levelIndex ?? 0, 0, LEVELS.length - 1),
      score: data.score ?? 0,
      multiplier: data.multiplier ?? 1,
    };
  }

  create(): void {
    configureLogicalCamera(this);
    this.physics.world.setBounds(0, 0, LOGICAL_WIDTH, LOGICAL_HEIGHT);
    this.level = LEVELS[this.run.levelIndex];
    this.scoreChain = new ScoreChain(this.run.score);
    this.inputRouter = new InputRouter(this);
    this.platforms = this.physics.add.staticGroup();
    this.players = [];
    this.enemies = [];
    this.bubbles = [];
    this.enemyProjectiles = [];
    this.completing = false;
    this.isPaused = false;
    this.stageStartedAt = this.time.now;
    this.playableAt = this.time.now + 900;

    drawLevelBackground(this, this.level);
    this.createWindVisuals(this.level.winds ?? []);
    this.createPlatforms();
    this.createPlayers();
    this.createEnemies();
    this.createBoss();
    this.createHud();
    this.showLevelIntro();

    window.addEventListener('blur', this.blurHandler);
    this.events.once(Phaser.Scenes.Events.SHUTDOWN, () => {
      window.removeEventListener('blur', this.blurHandler);
      this.inputRouter.clear();
    });
  }

  update(time: number, delta: number): void {
    if (Phaser.Input.Keyboard.JustDown(this.inputRouter.pause)) {
      this.pauseGame(!this.isPaused);
    }
    if (Phaser.Input.Keyboard.JustDown(this.inputRouter.mute)) this.toggleMute();
    if (this.isPaused || this.completing) return;

    audio.updateMusic(time, this.level.music);
    this.updatePlayers(time);
    this.updateEnemies(time, delta);
    this.updateEnemyProjectiles(time, delta);
    this.updateBubbles(time, delta);
    this.updateBoss(time);
    this.resolveContacts(time);
    this.updateHud();
    this.checkLevelState(time);
  }

  getDebugSnapshot(): {
    level: number;
    paused: boolean;
    score: number;
    players: Array<{ slot: number; x: number; y: number; hearts: number; active: boolean }>;
    bubbles: number;
    enemies: number;
    bossHealth: number | null;
    bossHits: number | null;
    bossTrapped: boolean;
    bossZone: number | null;
    enemyStates: Array<{ kind: EnemyKind; x: number; y: number; velocityX: number; recoveries: number }>;
  } {
    return {
      level: this.level.id,
      paused: this.isPaused,
      score: this.scoreChain.score,
      players: this.players.map((player) => ({
        slot: player.slot,
        x: Math.round(player.sprite.x),
        y: Math.round(player.sprite.y),
        hearts: player.hearts,
        active: player.active,
      })),
      bubbles: this.bubbles.length,
      enemies: this.enemies.filter((enemy) => enemy.alive).length,
      bossHealth: this.boss?.health ?? null,
      bossHits: this.boss?.bubbleHits ?? null,
      bossTrapped: this.boss?.trapped ?? false,
      bossZone: this.boss?.relocationIndex ?? null,
      enemyStates: this.enemies
        .filter((enemy) => enemy.alive && enemy.sprite.active)
        .map((enemy) => ({
          kind: enemy.kind,
          x: Math.round(enemy.sprite.x),
          y: Math.round(enemy.sprite.y),
          velocityX: Math.round((enemy.sprite.body as Phaser.Physics.Arcade.Body).velocity.x),
          recoveries: enemy.recoveries,
        })),
    };
  }

  debugHitBoss(count = 1): void {
    for (let index = 0; index < count; index += 1) {
      if (!this.boss || this.boss.health <= 0 || this.boss.trapped) break;
      this.registerBossHit(this.time.now + index);
    }
  }

  debugPopBoss(): void {
    if (this.boss?.trapped) this.popBossBubble(this.time.now + 400);
  }

  debugProtectPlayers(): void {
    for (const player of this.players) player.invulnerableUntil = Number.POSITIVE_INFINITY;
  }

  debugFreeze(): void {
    this.physics.world.pause();
    this.tweens.pauseAll();
    this.isPaused = true;
  }

  private createPlatforms(): void {
    for (const definition of this.level.platforms) {
      const rectangle = drawPlatform(this, definition);
      this.physics.add.existing(rectangle, true);
      this.platforms.add(rectangle);
      const body = rectangle.body as Phaser.Physics.Arcade.StaticBody;
      body.checkCollision.down = false;
      body.checkCollision.left = false;
      body.checkCollision.right = false;
      body.checkCollision.up = true;
    }
  }

  private createPlayers(): void {
    const count = this.run.mode === 'coop' ? 2 : 1;
    for (let slot = 0; slot < count; slot += 1) {
      const typedSlot = slot as 0 | 1;
      const spawn = this.level.playerSpawns[typedSlot];
      const sprite = this.physics.add.sprite(spawn.x, spawn.y, `frog-${slot}-idle`).setDepth(12);
      sprite.setCollideWorldBounds(true);
      sprite.setBounce(0);
      sprite.setMaxVelocity(150, 430);
      const body = sprite.body as Phaser.Physics.Arcade.Body;
      body.setSize(30, 24).setOffset(7, 14);
      this.physics.add.collider(sprite, this.platforms);
      this.players.push({
        slot: typedSlot,
        sprite,
        hearts: 3,
        active: true,
        rescueUsed: false,
        rescueStartedAt: 0,
        invulnerableUntil: 0,
        lastBubbleAt: Number.NEGATIVE_INFINITY,
        facing: 1,
        poseUntil: 0,
      });
    }
  }

  private createEnemies(): void {
    for (const spawn of this.level.enemies) {
      if (spawn.coopOnly && this.run.mode !== 'coop') continue;
      this.spawnEnemy(spawn);
    }
  }

  private spawnEnemy(spawn: EnemySpawn): EnemyRuntime {
    const sprite = this.physics.add.sprite(spawn.x, spawn.y, `enemy-${spawn.kind}`).setDepth(10);
    const flyer = spawn.behavior === 'flyer';
    sprite.setCollideWorldBounds(true);
    sprite.setBounce(flyer ? 1 : 0);
    sprite.setGravityY(flyer ? -760 : 0);
    const body = sprite.body as Phaser.Physics.Arcade.Body;
    body.setSize(23, 24).setOffset(5, 6);
    if (!flyer) this.physics.add.collider(sprite, this.platforms);
    const speeds: Record<EnemyKind, number> = {
      beetle: 42,
      devil: 58,
      fountain: 48,
      bandit: 55,
      yarn: 52,
      luggage: 46,
      wisp: 44,
    };
    const initialDirection = spawn.direction ?? (spawn.x > 240 ? -1 : 1);
    const enemy: EnemyRuntime = {
      sprite,
      kind: spawn.kind,
      behavior: spawn.behavior,
      direction: initialDirection,
      baseSpeed: speeds[spawn.kind],
      alive: true,
      trapped: false,
      angry: false,
      nextJumpAt: this.time.now + 800 + Math.random() * 900,
      originY: spawn.y,
      phase: Math.random() * Math.PI * 2,
      action: 'roam',
      actionUntil: 0,
      nextActionAt: this.time.now + 1_100 + Math.random() * 1_500,
      roamTargetX: Phaser.Math.Clamp(spawn.x + initialDirection * 90, 48, 432),
      nextRetargetAt: this.time.now + 900 + Math.random() * 1_200,
      lastProgressX: spawn.x,
      lastProgressAt: this.time.now,
      navigationLockUntil: 0,
      recoveries: 0,
    };
    this.enemies.push(enemy);
    return enemy;
  }

  private createBoss(): void {
    const definition = this.level.boss;
    if (!definition) return;
    const sprite = this.physics.add.sprite(definition.x, definition.y, `boss-${definition.texture}`).setDepth(11);
    sprite.setCollideWorldBounds(true);
    sprite.setMaxVelocity(90, 360);
    const body = sprite.body as Phaser.Physics.Arcade.Body;
    body.setSize(46, 45).setOffset(7, 10);
    this.physics.add.collider(sprite, this.platforms);
    this.boss = {
      sprite,
      name: definition.name,
      health: definition.health,
      maxHealth: definition.health,
      direction: -1,
      nextJumpAt: this.time.now + 1_400,
      bubbleHits: 0,
      trapped: false,
      trappedAt: 0,
      popAllowedAt: 0,
      relocationIndex: 0,
      relocationPoints: definition.relocationPoints,
      stunnedUntil: 0,
    };
  }

  private createHud(): void {
    this.add.rectangle(240, 11, 480, 23, 0x06111e, 0.92).setDepth(500).setStrokeStyle(1, 0x31584d);
    this.add.text(8, 4, `ETAP ${this.level.id}/${LEVEL_COUNT}`, titleStyle(7, '#efc65d')).setDepth(501);
    this.hudScore = this.add.text(240, 4, '', titleStyle(7, '#f6e5b6')).setOrigin(0.5, 0).setDepth(501);
    this.hudHearts = this.add.text(472, 4, '', titleStyle(7, '#f2b2a7')).setOrigin(1, 0).setDepth(501);
    this.hudMultiplier = this.add.text(356, 4, '', titleStyle(7, '#8fe17f')).setOrigin(1, 0).setDepth(501);
    if (this.boss) {
      this.bossHud = this.add.text(240, 26, '', {
        ...titleStyle(8, '#f7d888'),
        backgroundColor: '#06111ecc',
        padding: { x: 8, y: 4 },
        align: 'center',
      }).setOrigin(0.5, 0).setDepth(501);
      const bossHint = this.add.text(240, 244, '3 BAŃKI OSŁABIĄ · 4. UWIĘZI · DOTKNIJ, BY PRZERZUCIĆ', {
        ...titleStyle(8, '#f6e5b6'),
        backgroundColor: '#06111ee6',
        padding: { x: 8, y: 5 },
      }).setOrigin(0.5).setDepth(400);
      this.tweens.add({ targets: bossHint, alpha: 0, delay: 7_500, duration: 700, onComplete: () => bossHint.destroy() });
    }
    if (this.level.id === 1) {
      const hint = this.add.text(240, 238, 'ENTER · wypuść bańkę   DOTKNIJ · rozbij', {
        ...titleStyle(7, '#f6e5b6'),
        backgroundColor: '#06111ed9',
        padding: { x: 7, y: 4 },
      }).setOrigin(0.5).setDepth(400);
      this.tweens.add({ targets: hint, alpha: 0, delay: 5_000, duration: 700, onComplete: () => hint.destroy() });
    }
    this.updateHud();
  }

  private showLevelIntro(): void {
    const panel = this.add.rectangle(240, 118, 430, 72, 0x06111e, 0.9).setStrokeStyle(2, this.level.palette[3]).setDepth(600);
    const number = this.add.text(44, 92, this.level.id.toString().padStart(2, '0'), titleStyle(24, '#efc65d')).setDepth(601);
    const name = this.add.text(83, 94, this.level.name.toUpperCase(), titleStyle(12, '#f6e5b6')).setDepth(601);
    const subtitle = this.add.text(84, 120, this.level.subtitle, titleStyle(8, '#8fd1b0')).setDepth(601);
    this.tweens.add({
      targets: [panel, number, name, subtitle],
      alpha: 0,
      y: '-=8',
      delay: 650,
      duration: 350,
      onComplete: () => [panel, number, name, subtitle].forEach((object) => object.destroy()),
    });
  }

  private createWindVisuals(zones: WindZone[]): void {
    zones.forEach((zone, index) => {
      const graphics = this.add.graphics().setDepth(1);
      graphics.lineStyle(1, this.level.palette[3], 0.28);
      const horizontal = Math.abs(zone.forceX) > Math.abs(zone.forceY);
      for (let i = 0; i < 4; i += 1) {
        if (horizontal) {
          const y = zone.y + 18 + i * Math.max(15, (zone.height - 30) / 4);
          graphics.lineBetween(zone.x, y, zone.x + zone.width, y + (i % 2 ? 3 : -3));
        } else {
          const x = zone.x + 9 + i * Math.max(8, (zone.width - 16) / 4);
          graphics.lineBetween(x, zone.y + zone.height, x + (i % 2 ? 4 : -4), zone.y);
        }
      }
      this.tweens.add({
        targets: graphics,
        alpha: { from: 0.25, to: 0.85 },
        duration: (zone.pulseMs ?? 2_000) / 2,
        yoyo: true,
        repeat: -1,
        delay: index * 180,
      });
    });
  }

  private updatePlayers(time: number): void {
    if (time < this.playableAt) return;
    for (const player of this.players) {
      if (!player.active) continue;
      const body = player.sprite.body as Phaser.Physics.Arcade.Body;
      this.keepPlayerInBounds(player, body, time);
      const left = this.inputRouter.down(player.slot, 'left');
      const right = this.inputRouter.down(player.slot, 'right');
      const move = left === right ? 0 : left ? -1 : 1;
      body.setVelocityX(move * 108);
      if (move !== 0) {
        player.facing = move as -1 | 1;
        player.sprite.setFlipX(move < 0);
      }
      if (this.inputRouter.justDown(player.slot, 'jump') && (body.blocked.down || body.touching.down)) {
        body.setVelocityY(-292);
        audio.jump();
      }
      if (this.inputRouter.justDown(player.slot, 'drop') && this.canDropThroughPlatform(body)) {
        player.sprite.y += 9;
      }
      if (
        this.inputRouter.justDown(player.slot, 'bubble') &&
        time - player.lastBubbleAt >= BUBBLE_COOLDOWN_MS &&
        this.bubbles.filter((bubble) => bubble.owner === player.slot).length < MAX_PLAYER_BUBBLES
      ) {
        this.shootBubble(player, time);
      }
      this.updatePlayerPose(player, time, move);
      player.sprite.setAlpha(time < player.invulnerableUntil && Math.floor(time / 90) % 2 === 0 ? 0.3 : 1);
    }
  }

  private canDropThroughPlatform(body: Phaser.Physics.Arcade.Body): boolean {
    return this.level.platforms.some((platform) => {
      const height = platform.height ?? 10;
      const top = platform.y - height / 2;
      const left = platform.x - platform.width / 2;
      const right = platform.x + platform.width / 2;
      const isRaisedPlatform = top < LOGICAL_HEIGHT - 25;
      const hasHorizontalSupport = body.right > left + 3 && body.left < right - 3;
      return isRaisedPlatform && hasHorizontalSupport && Math.abs(body.bottom - top) <= 8;
    });
  }

  private keepPlayerInBounds(
    player: PlayerRuntime,
    body: Phaser.Physics.Arcade.Body,
    time: number,
  ): void {
    const outsideSafetyNet =
      !Number.isFinite(player.sprite.x) || !Number.isFinite(player.sprite.y) ||
      player.sprite.x < -24 || player.sprite.x > LOGICAL_WIDTH + 24 ||
      player.sprite.y < -48 || player.sprite.y > LOGICAL_HEIGHT + 18;
    if (outsideSafetyNet) {
      const spawn = this.level.playerSpawns[player.slot];
      player.sprite.setPosition(spawn.x, spawn.y);
      body.setVelocity(0, 0);
      player.invulnerableUntil = Math.max(player.invulnerableUntil, time + 1_000);
      return;
    }

    player.sprite.x = Phaser.Math.Clamp(player.sprite.x, 16, LOGICAL_WIDTH - 16);
  }

  private updatePlayerPose(player: PlayerRuntime, time: number, move: number): void {
    const body = player.sprite.body as Phaser.Physics.Arcade.Body;
    let pose = 'idle';
    if (time < player.poseUntil) pose = 'blow';
    else if (!body.blocked.down && !body.touching.down) pose = 'jump';
    else if (move !== 0) pose = Math.floor(time / 120) % 2 === 0 ? 'run1' : 'run2';
    player.sprite.setTexture(`frog-${player.slot}-${pose}`);
  }

  private shootBubble(player: PlayerRuntime, time: number): void {
    player.lastBubbleAt = time;
    player.poseUntil = time + 150;
    const sprite = this.physics.add.sprite(player.sprite.x + player.facing * 16, player.sprite.y - 5, 'bubble').setDepth(14);
    sprite.setGravityY(-760);
    sprite.setVelocity(player.facing * 165, -5);
    const body = sprite.body as Phaser.Physics.Arcade.Body;
    body.setCircle(8, 2, 2);
    this.bubbles.push({
      sprite,
      owner: player.slot,
      direction: player.facing,
      createdAt: time,
      popAllowedAt: time + 220,
    });
    audio.bubble();
  }

  private updateEnemies(time: number, delta: number): void {
    for (const enemy of this.enemies) {
      if (!enemy.alive || enemy.trapped || !enemy.sprite.active) continue;
      const body = enemy.sprite.body as Phaser.Physics.Arcade.Body;
      const speed = enemy.baseSpeed * (enemy.angry ? 1.55 : 1);
      const target = this.nearestActivePlayer(enemy.sprite.x, enemy.sprite.y);
      const grounded = body.blocked.down || body.touching.down;
      this.updateEnemyNavigation(enemy, body, target, grounded, time);

      if (enemy.kind === 'luggage') {
        if (enemy.action === 'roam' && time >= enemy.nextActionAt) {
          enemy.action = 'warn';
          enemy.actionUntil = time + 430;
          body.setVelocityX(0);
          enemy.sprite.setTint(0xffd76a);
        } else if (enemy.action === 'warn' && time >= enemy.actionUntil) {
          enemy.action = 'charge';
          enemy.actionUntil = time + 760;
          enemy.direction = target && target.sprite.x < enemy.sprite.x ? -1 : 1;
          enemy.sprite.clearTint();
        } else if (enemy.action === 'charge' && time >= enemy.actionUntil) {
          enemy.action = 'roam';
          enemy.nextActionAt = time + 1_850;
          body.setVelocityX(0);
        }
      }

      if (enemy.kind === 'wisp') {
        const desiredX = target
          ? Phaser.Math.Clamp(target.sprite.x + Math.sin(time / 700 + enemy.phase) * 58, 48, 432)
          : enemy.roamTargetX;
        const dx = desiredX - enemy.sprite.x;
        const dy = target ? target.sprite.y - enemy.sprite.y : enemy.originY - enemy.sprite.y;
        body.setVelocity(
          Phaser.Math.Clamp(dx * 0.45, -speed * 1.25, speed * 1.25),
          Phaser.Math.Clamp(dy * 0.32 + Math.sin(time / 280 + enemy.phase) * 25, -58, 58),
        );
        enemy.direction = dx < 0 ? -1 : 1;
        enemy.sprite.setAlpha(0.62 + Math.sin(time / 170 + enemy.phase) * 0.25);
      } else if (enemy.kind === 'devil' && target && time >= enemy.nextActionAt) {
        enemy.direction = target.sprite.x < enemy.sprite.x ? -1 : 1;
        const destinationX = Phaser.Math.Clamp(target.sprite.x - enemy.direction * 76, 48, 432);
        this.sparkBurst(enemy.sprite.x, enemy.sprite.y, 0xd86664, 5);
        enemy.sprite.setPosition(destinationX, Phaser.Math.Clamp(target.sprite.y - 48, 42, 212));
        body.setVelocity(enemy.direction * speed * 1.5, -70);
        enemy.nextActionAt = time + 2_300 + Math.random() * 500;
        this.sparkBurst(enemy.sprite.x, enemy.sprite.y, 0xd86664, 5);
      } else if (enemy.kind === 'luggage' && enemy.action === 'warn') {
        body.setVelocityX(0);
        enemy.sprite.setScale(1 + Math.sin(time / 45) * 0.05, 1);
      } else if (enemy.kind === 'luggage' && enemy.action === 'charge') {
        body.setVelocityX(enemy.direction * speed * 2.7);
        enemy.sprite.setScale(1.08, 0.94);
      } else if (enemy.behavior === 'flyer') {
        body.setVelocityX(enemy.direction * speed);
        body.setVelocityY(Math.sin(time / 360 + enemy.phase) * 28);
        if (enemy.sprite.x < 20 || enemy.sprite.x > 460) enemy.direction *= -1;
      } else {
        let moveSpeed = speed;
        if (enemy.kind === 'yarn') moveSpeed *= 1.45;
        if (enemy.kind === 'bandit' && target && Math.abs(target.sprite.x - enemy.sprite.x) < 170) moveSpeed *= 1.22;
        if (enemy.kind === 'beetle' && time < enemy.actionUntil) moveSpeed *= 1.9;
        body.setVelocityX(enemy.direction * moveSpeed);

        if (enemy.kind === 'beetle' && target && time >= enemy.nextActionAt && grounded) {
          enemy.direction = target.sprite.x < enemy.sprite.x ? -1 : 1;
          body.setVelocityY(-145);
          enemy.actionUntil = time + 620;
          enemy.nextActionAt = time + 2_100 + Math.random() * 650;
        }
        if (enemy.kind === 'fountain' && target && time >= enemy.nextActionAt) {
          this.spawnEnemyProjectile(enemy, target, time);
          enemy.nextActionAt = time + 1_750 + Math.random() * 500;
        }
        if (enemy.behavior === 'hopper' && time >= enemy.nextJumpAt && grounded) {
          body.setVelocityY(-215 - (enemy.angry ? 35 : 0) - (enemy.kind === 'bandit' ? 24 : 0));
          enemy.nextJumpAt = time + (enemy.kind === 'bandit' ? 760 : 1_000) + Math.random() * 800;
        }
      }
      const safeDirection = safeHorizontalDirection(
        enemy.sprite.x,
        enemy.direction,
        body.blocked.left,
        body.blocked.right,
      );
      if (safeDirection !== enemy.direction) {
        this.turnEnemyInward(enemy, body, safeDirection, grounded, time);
      }
      enemy.sprite.setFlipX(enemy.direction < 0);
      if (enemy.kind !== 'luggage') enemy.sprite.setScale(1);
      enemy.sprite.rotation = enemy.kind === 'yarn'
        ? enemy.sprite.rotation + body.velocity.x * delta * 0.0009
        : enemy.behavior === 'flyer' ? Math.sin(time / 200 + enemy.phase) * 0.08 : 0;
      if (enemy.sprite.y > 282) {
        enemy.sprite.setPosition(enemy.sprite.x < 240 ? 50 : 430, 35);
        body.setVelocityY(0);
      }
    }
  }

  private updateEnemyNavigation(
    enemy: EnemyRuntime,
    body: Phaser.Physics.Arcade.Body,
    target: PlayerRuntime | undefined,
    grounded: boolean,
    time: number,
  ): void {
    const safeDirection = safeHorizontalDirection(
      enemy.sprite.x,
      enemy.direction,
      body.blocked.left,
      body.blocked.right,
    );
    if (safeDirection !== enemy.direction) {
      this.turnEnemyInward(enemy, body, safeDirection, grounded, time);
    }

    if (time >= enemy.nextRetargetAt && time >= enemy.navigationLockUntil) {
      const huntsPlayers = enemy.kind === 'bandit' || enemy.kind === 'devil' || enemy.kind === 'wisp';
      enemy.roamTargetX = huntsPlayers && target
        ? Phaser.Math.Clamp(target.sprite.x + Phaser.Math.Between(-55, 55), 48, 432)
        : Phaser.Math.Between(52, 428);
      if (Math.abs(enemy.roamTargetX - enemy.sprite.x) > 24) {
        enemy.direction = enemy.roamTargetX < enemy.sprite.x ? -1 : 1;
      }
      enemy.nextRetargetAt = time + Phaser.Math.Between(1_250, 2_650);
    }

    if (
      grounded && time >= enemy.navigationLockUntil &&
      enemy.behavior !== 'flyer' && this.isApproachingPlatformEdge(enemy, body)
    ) {
      const targetBeyondEdge = target && Math.sign(target.sprite.x - enemy.sprite.x) === enemy.direction;
      const mayLeap = enemy.behavior === 'hopper' && targetBeyondEdge && time >= enemy.nextJumpAt;
      if (mayLeap) {
        body.setVelocityY(-225 - (enemy.angry ? 30 : 0));
        enemy.nextJumpAt = time + Phaser.Math.Between(1_050, 1_700);
        enemy.navigationLockUntil = time + 480;
      } else {
        this.turnEnemyInward(enemy, body, enemy.direction === 1 ? -1 : 1, grounded, time);
      }
    }

    if (time - enemy.lastProgressAt >= 650) {
      const triesToMove = Math.abs(body.velocity.x) >= 18 && enemy.action !== 'warn';
      if (grounded && triesToMove && !madeHorizontalProgress(enemy.lastProgressX, enemy.sprite.x)) {
        const recoveryDirection: -1 | 1 = enemy.sprite.x < 120 ? 1 : enemy.sprite.x > 360 ? -1 : enemy.direction === 1 ? -1 : 1;
        this.turnEnemyInward(enemy, body, recoveryDirection, grounded, time);
        enemy.sprite.x = Phaser.Math.Clamp(enemy.sprite.x + recoveryDirection * 5, 25, 455);
        if (enemy.behavior === 'hopper') body.setVelocityY(-175);
        enemy.recoveries += 1;
      }
      enemy.lastProgressX = enemy.sprite.x;
      enemy.lastProgressAt = time;
    }
  }

  private turnEnemyInward(
    enemy: EnemyRuntime,
    body: Phaser.Physics.Arcade.Body,
    direction: -1 | 1,
    grounded: boolean,
    time: number,
  ): void {
    enemy.direction = direction;
    enemy.roamTargetX = Phaser.Math.Clamp(enemy.sprite.x + direction * Phaser.Math.Between(90, 150), 48, 432);
    enemy.navigationLockUntil = time + 520;
    enemy.nextRetargetAt = time + Phaser.Math.Between(900, 1_500);
    if (enemy.kind === 'luggage' && enemy.action !== 'roam') {
      enemy.action = 'roam';
      enemy.actionUntil = 0;
      enemy.nextActionAt = time + 1_300;
      enemy.sprite.clearTint().setScale(1);
    }
    body.setVelocityX(direction * Math.max(36, enemy.baseSpeed));
    if (grounded && enemy.behavior === 'hopper' && (body.blocked.left || body.blocked.right)) {
      body.setVelocityY(-165);
    }
  }

  private isApproachingPlatformEdge(enemy: EnemyRuntime, body: Phaser.Physics.Arcade.Body): boolean {
    const support = this.level.platforms
      .map((platform) => ({
        ...platform,
        top: platform.y - (platform.height ?? 10) / 2,
        left: platform.x - platform.width / 2,
        right: platform.x + platform.width / 2,
      }))
      .filter((platform) =>
        Math.abs(platform.top - body.bottom) <= 7 &&
        enemy.sprite.x >= platform.left - 2 && enemy.sprite.x <= platform.right + 2)
      .sort((a, b) => Math.abs(a.top - body.bottom) - Math.abs(b.top - body.bottom))[0];
    if (!support) return false;
    const inset = body.halfWidth + 7;
    const lookAhead = enemy.sprite.x + enemy.direction * (inset + 5);
    return lookAhead <= support.left + inset || lookAhead >= support.right - inset;
  }

  private nearestActivePlayer(x: number, y: number): PlayerRuntime | undefined {
    return this.players
      .filter((player) => player.active)
      .sort((a, b) => Phaser.Math.Distance.Squared(x, y, a.sprite.x, a.sprite.y) -
        Phaser.Math.Distance.Squared(x, y, b.sprite.x, b.sprite.y))[0];
  }

  private spawnEnemyProjectile(enemy: EnemyRuntime, target: PlayerRuntime, time: number): void {
    const sprite = this.physics.add.sprite(enemy.sprite.x, enemy.sprite.y - 7, 'enemy-shot').setDepth(13);
    sprite.setGravityY(-760);
    const angle = Phaser.Math.Angle.Between(sprite.x, sprite.y, target.sprite.x, target.sprite.y);
    sprite.setVelocity(Math.cos(angle) * 112, Math.sin(angle) * 112);
    const body = sprite.body as Phaser.Physics.Arcade.Body;
    body.setCircle(5, 2, 2);
    this.enemyProjectiles.push({ sprite, createdAt: time });
  }

  private updateEnemyProjectiles(time: number, delta: number): void {
    for (const projectile of [...this.enemyProjectiles]) {
      projectile.sprite.rotation += delta * 0.005;
      if (
        time - projectile.createdAt > 3_200 ||
        projectile.sprite.x < -16 || projectile.sprite.x > 496 ||
        projectile.sprite.y < 20 || projectile.sprite.y > 286
      ) this.destroyEnemyProjectile(projectile);
    }
  }

  private destroyEnemyProjectile(projectile: EnemyProjectile): void {
    projectile.sprite.destroy();
    const index = this.enemyProjectiles.indexOf(projectile);
    if (index >= 0) this.enemyProjectiles.splice(index, 1);
  }

  private updateBubbles(time: number, delta: number): void {
    for (const bubble of [...this.bubbles]) {
      if (!bubble.sprite.active) continue;
      const phase = bubblePhase(
        time - bubble.createdAt,
        bubble.trappedAt === undefined ? undefined : bubble.trappedAt - bubble.createdAt,
      );
      const body = bubble.sprite.body as Phaser.Physics.Arcade.Body;
      if (phase === 'flight') {
        body.setVelocity(bubble.direction * 165, -5);
      } else if (phase === 'floating' || phase === 'trapped') {
        body.setVelocity(bubble.direction * (phase === 'trapped' ? 7 : 15), phase === 'trapped' ? -31 : -38);
        bubble.sprite.rotation += 0.01 * bubble.direction;
      } else if (bubble.enemy) {
        this.releaseEnemy(bubble);
        continue;
      } else {
        this.destroyBubble(bubble);
        continue;
      }

      this.applyWind(bubble, time, delta);
      if (bubble.sprite.y < 30) bubble.direction *= -1;
      if (bubble.sprite.x < 10 || bubble.sprite.x > 470) bubble.direction *= -1;
    }

    for (const player of this.players) {
      if (!player.rescueBubble?.active) continue;
      player.rescueBubble.y -= 18 * (delta / 1_000);
      player.rescueBubble.x += Math.sin(time / 260 + player.slot) * 0.25;
      if (!canRescue(player.rescueStartedAt, time)) {
        player.rescueBubble.destroy();
        player.rescueBubble = undefined;
        this.checkFailure();
      }
    }
  }

  private applyWind(bubble: BubbleRuntime, time: number, delta: number): void {
    const body = bubble.sprite.body as Phaser.Physics.Arcade.Body;
    for (const zone of this.level.winds ?? []) {
      if (!Phaser.Geom.Rectangle.Contains(
        new Phaser.Geom.Rectangle(zone.x, zone.y, zone.width, zone.height),
        bubble.sprite.x,
        bubble.sprite.y,
      )) continue;
      const active = !zone.pulseMs || Math.floor(time / (zone.pulseMs / 2)) % 2 === 0;
      if (!active) continue;
      body.velocity.x += zone.forceX * (delta / 1_000);
      body.velocity.y += zone.forceY * (delta / 1_000);
    }
  }

  private updateBoss(time: number): void {
    const boss = this.boss;
    if (!boss || boss.health <= 0) return;
    if (boss.trapped) {
      const trap = boss.trapBubble;
      if (!trap?.active) return;
      trap.x += Math.sin(time / 260) * 0.12;
      trap.rotation = Math.sin(time / 310) * 0.04;
      if (time - boss.trappedAt >= BOSS_TRAP_MS) this.releaseBossFromBubble(time);
      return;
    }
    if (!boss.sprite.active) return;
    const body = boss.sprite.body as Phaser.Physics.Arcade.Body;
    if (time < boss.stunnedUntil) {
      body.setVelocityX(0);
      boss.sprite.setAlpha(Math.floor(time / 80) % 2 ? 0.55 : 1);
      return;
    }
    boss.sprite.setAlpha(1);
    body.setVelocityX(boss.direction * 46);
    if (body.blocked.left || body.blocked.right || boss.sprite.x < 44 || boss.sprite.x > 436) boss.direction *= -1;
    boss.sprite.setFlipX(boss.direction < 0);
    if (time >= boss.nextJumpAt && (body.blocked.down || body.touching.down)) {
      body.setVelocityY(-235);
      boss.nextJumpAt = time + 1_350 + Math.random() * 750;
    }
  }

  private resolveContacts(time: number): void {
    for (const bubble of [...this.bubbles]) {
      if (!bubble.sprite.active) continue;
      if (!bubble.enemy) {
        const boss = this.boss;
        if (
          boss?.sprite.active && !boss.trapped && boss.health > 0 && time >= boss.stunnedUntil &&
          Phaser.Math.Distance.Between(bubble.sprite.x, bubble.sprite.y, boss.sprite.x, boss.sprite.y) < 35
        ) {
          this.destroyBubble(bubble);
          this.registerBossHit(time);
          continue;
        }
        for (const enemy of this.enemies) {
          if (!enemy.alive || enemy.trapped || !enemy.sprite.active) continue;
          if (Phaser.Math.Distance.Between(bubble.sprite.x, bubble.sprite.y, enemy.sprite.x, enemy.sprite.y) < 18) {
            this.captureEnemy(bubble, enemy, time);
            break;
          }
        }
      }
      if (bubble.enemy && time >= bubble.popAllowedAt) {
        for (const player of this.players) {
          if (!player.active) continue;
          if (Phaser.Math.Distance.Between(bubble.sprite.x, bubble.sprite.y, player.sprite.x, player.sprite.y) < 21) {
            this.popBubble(bubble, time);
            break;
          }
        }
      }
    }

    for (const player of this.players) {
      if (!player.active) continue;
      if (
        this.boss?.trapped && this.boss.trapBubble?.active && time >= this.boss.popAllowedAt &&
        Phaser.Math.Distance.Between(player.sprite.x, player.sprite.y, this.boss.trapBubble.x, this.boss.trapBubble.y) < 40
      ) this.popBossBubble(time);
      for (const other of this.players) {
        if (!other.rescueBubble?.active || other.slot === player.slot) continue;
        if (Phaser.Math.Distance.Between(player.sprite.x, player.sprite.y, other.rescueBubble.x, other.rescueBubble.y) < 24) {
          this.rescuePlayer(other, time);
        }
      }
      for (const enemy of this.enemies) {
        if (!enemy.alive || enemy.trapped || !enemy.sprite.active) continue;
        if (Phaser.Math.Distance.Between(player.sprite.x, player.sprite.y, enemy.sprite.x, enemy.sprite.y) < 19) {
          this.damagePlayer(player, time);
          break;
        }
      }
      for (const projectile of [...this.enemyProjectiles]) {
        if (!projectile.sprite.active) continue;
        if (Phaser.Math.Distance.Between(player.sprite.x, player.sprite.y, projectile.sprite.x, projectile.sprite.y) < 19) {
          this.damagePlayer(player, time);
          this.destroyEnemyProjectile(projectile);
          break;
        }
      }
      if (
        this.boss?.sprite.active && !this.boss.trapped && time >= this.boss.stunnedUntil &&
        this.boss.health > 0 &&
        Phaser.Math.Distance.Between(player.sprite.x, player.sprite.y, this.boss.sprite.x, this.boss.sprite.y) < 33
      ) {
        this.damagePlayer(player, time);
      }
    }
  }

  private captureEnemy(bubble: BubbleRuntime, enemy: EnemyRuntime, time: number): void {
    enemy.trapped = true;
    enemy.sprite.disableBody(true, true);
    bubble.enemy = enemy;
    bubble.trappedAt = time;
    bubble.popAllowedAt = time + 260;
    bubble.sprite.setScale(1.28);
    bubble.sprite.setTint(enemy.angry ? 0xff8e78 : 0xffffff);
    const gained = this.scoreChain.capture();
    this.floatText(bubble.sprite.x, bubble.sprite.y, `+${gained}`, '#83ddd3');
  }

  private releaseEnemy(bubble: BubbleRuntime): void {
    const enemy = bubble.enemy;
    if (enemy?.alive) {
      enemy.trapped = false;
      enemy.angry = true;
      enemy.sprite.enableBody(true, bubble.sprite.x, Math.max(38, bubble.sprite.y), true, true);
      enemy.sprite.setTint(0xff8975);
      this.time.delayedCall(800, () => enemy.sprite.active && enemy.sprite.clearTint());
    }
    this.destroyBubble(bubble);
  }

  private popBubble(bubble: BubbleRuntime, time: number): void {
    const enemy = bubble.enemy;
    if (!enemy?.alive) return;
    enemy.alive = false;
    enemy.trapped = false;
    enemy.sprite.destroy();
    const gained = this.scoreChain.pop(time);
    this.floatText(bubble.sprite.x, bubble.sprite.y, `+${gained}`, '#f5d66f');
    this.sparkBurst(bubble.sprite.x, bubble.sprite.y, this.level.palette[3]);
    audio.pop();

    this.destroyBubble(bubble);
  }

  private registerBossHit(time: number): void {
    const boss = this.boss;
    if (!boss || boss.health <= 0 || boss.trapped || !boss.sprite.active) return;
    const progress = registerBossBubbleHit(boss.bubbleHits);
    boss.bubbleHits = progress.hits;
    this.cameras.main.shake(progress.result === 'trap' ? 110 : 55, progress.result === 'trap' ? 0.005 : 0.002);
    if (progress.result === 'trap') {
      this.trapBoss(time);
      return;
    }
    boss.stunnedUntil = time + 210;
    boss.sprite.setTint(0xaeeff2);
    boss.sprite.setVelocity(-boss.direction * 55, -48);
    this.floatText(
      boss.sprite.x,
      boss.sprite.y - 34,
      `${boss.bubbleHits}/${BOSS_STAGGER_HITS} OSŁABIENIA`,
      '#aeeff2',
    );
    this.time.delayedCall(180, () => boss.sprite.active && boss.sprite.clearTint());
    audio.pop();
  }

  private trapBoss(time: number): void {
    const boss = this.boss;
    if (!boss?.sprite.active) return;
    const x = boss.sprite.x;
    const y = boss.sprite.y;
    boss.trapped = true;
    boss.trappedAt = time;
    boss.popAllowedAt = time + 300;
    boss.sprite.disableBody(true, true);
    const trap = this.physics.add.sprite(x, y, 'boss-bubble').setDepth(18);
    trap.setGravityY(-760).setVelocity(0, -10);
    const body = trap.body as Phaser.Physics.Arcade.Body;
    body.setCircle(31, 4, 4);
    boss.trapBubble = trap;
    this.sparkBurst(x, y, 0x9cecf0, 12);
    this.floatText(x, y - 42, 'BOSS W BAŃCE — ROZBIJ!', '#ffffff');
    audio.bubble();
  }

  private popBossBubble(time: number): void {
    const boss = this.boss;
    const trap = boss?.trapBubble;
    if (!boss?.trapped || !trap?.active || time < boss.popAllowedAt) return;
    const popX = trap.x;
    const popY = trap.y;
    trap.destroy();
    boss.trapBubble = undefined;
    boss.trapped = false;
    boss.bubbleHits = 0;
    boss.health -= 1;
    this.scoreChain.score += boss.health <= 0 ? 3_000 : 1_500;
    this.sparkBurst(popX, popY, 0xf1d56e, 16);
    this.cameras.main.shake(160, 0.009);
    audio.pop();

    if (boss.health <= 0) {
      boss.sprite.destroy();
      this.floatText(popX, popY - 28, '+3000 · ODESŁANY!', '#f7d888');
      return;
    }

    const point = boss.relocationPoints[boss.relocationIndex % boss.relocationPoints.length];
    boss.relocationIndex += 1;
    boss.stunnedUntil = time + 900;
    boss.direction = point.x < 240 ? 1 : -1;
    boss.sprite.enableBody(true, point.x, point.y, true, true);
    boss.sprite.setVelocity(boss.direction * 70, -125);
    boss.sprite.setTint(0xf1d56e);
    this.time.delayedCall(420, () => boss.sprite.active && boss.sprite.clearTint());
    this.sparkBurst(point.x, point.y, 0xf1d56e, 10);
    this.floatText(point.x, point.y - 36, '+1500 · PRZERZUT!', '#f7d888');
  }

  private releaseBossFromBubble(time: number): void {
    const boss = this.boss;
    const trap = boss?.trapBubble;
    if (!boss?.trapped || !trap?.active) return;
    const x = Phaser.Math.Clamp(trap.x, 38, 442);
    const y = Phaser.Math.Clamp(trap.y, 55, 218);
    trap.destroy();
    boss.trapBubble = undefined;
    boss.trapped = false;
    boss.bubbleHits = 0;
    boss.stunnedUntil = time + 550;
    boss.sprite.enableBody(true, x, y, true, true);
    boss.sprite.setTint(0xff8975).setVelocity(boss.direction * 85, -80);
    this.time.delayedCall(500, () => boss.sprite.active && boss.sprite.clearTint());
    this.floatText(x, y - 34, 'UCIEKŁ — OD NOWA!', '#ff9d86');
  }

  private destroyBubble(bubble: BubbleRuntime): void {
    bubble.sprite.destroy();
    const index = this.bubbles.indexOf(bubble);
    if (index >= 0) this.bubbles.splice(index, 1);
  }

  private damagePlayer(player: PlayerRuntime, time: number): void {
    if (!player.active || time < player.invulnerableUntil || time < this.playableAt) return;
    const result = applyDamage(player.hearts, this.run.mode === 'coop', player.rescueUsed);
    player.hearts = result.hearts;
    audio.hurt();
    this.cameras.main.shake(90, 0.004);
    if (result.state === 'hurt') {
      player.invulnerableUntil = time + 1_550;
      const spawn = this.level.playerSpawns[player.slot];
      player.sprite.setPosition(spawn.x, spawn.y - 8).setVelocity(0, 0);
      return;
    }
    player.active = false;
    player.sprite.disableBody(true, true);
    if (result.state === 'rescue') {
      player.rescueUsed = true;
      player.rescueStartedAt = time;
      const rescue = this.physics.add.sprite(player.sprite.x, player.sprite.y, 'bubble').setScale(1.65).setDepth(16);
      rescue.setGravityY(-760).setTint(player.slot === 0 ? 0x91df78 : 0x6bded8);
      player.rescueBubble = rescue;
      this.floatText(rescue.x, rescue.y - 16, 'RATUJ!', '#ffffff');
    }
    this.checkFailure();
  }

  private rescuePlayer(player: PlayerRuntime, time: number): void {
    const bubble = player.rescueBubble;
    if (!bubble?.active) return;
    const x = bubble.x;
    const y = bubble.y;
    bubble.destroy();
    player.rescueBubble = undefined;
    player.hearts = 1;
    player.active = true;
    player.invulnerableUntil = time + 1_800;
    player.sprite.enableBody(true, x, y, true, true);
    player.sprite.setVelocity(0, -80);
    this.sparkBurst(x, y, player.slot === 0 ? COLORS.green : COLORS.teal, 10);
    this.floatText(x, y - 18, 'URATOWANY!', '#8fe17f');
    audio.rescue();
  }

  private checkLevelState(time: number): void {
    if (this.enemies.some((enemy) => enemy.alive)) return;
    if (this.boss && this.boss.health > 0) return;
    this.completing = true;
    this.physics.world.pause();
    const elapsed = time - this.stageStartedAt;
    const bonus = Math.max(0, 15_000 - Math.floor(elapsed / 100) * 25);
    this.scoreChain.score += bonus;
    this.store.recordLevelComplete(this.level.id, this.scoreChain.score);
    const banner = this.add.text(240, 132, 'MIASTO SPOKOJNE!', {
      ...titleStyle(15, '#f6e5b6'),
      backgroundColor: '#0b2b33ee',
      padding: { x: 16, y: 12 },
    }).setOrigin(0.5).setDepth(900).setScale(0.7);
    this.tweens.add({ targets: banner, scale: 1, duration: 250, ease: 'Back.Out' });
    audio.victory();
    this.time.delayedCall(1_150, () => {
      if (this.run.levelIndex >= LEVELS.length - 1) {
        this.scene.start('Result', { mode: this.run.mode, score: this.scoreChain.score });
      } else {
        this.scene.start('Postcard', {
          mode: this.run.mode,
          levelIndex: this.run.levelIndex + 1,
          score: this.scoreChain.score,
          bonus,
        });
      }
    });
  }

  private checkFailure(): void {
    const anyActive = this.players.some((player) => player.active);
    const anyRescuable = this.players.some((player) => player.rescueBubble?.active);
    if (anyActive || (anyRescuable && this.players.some((player) => player.active))) return;
    this.completing = true;
    this.physics.world.pause();
    this.time.delayedCall(650, () => {
      this.scene.start('GameOver', {
        mode: this.run.mode,
        levelIndex: this.run.levelIndex,
        score: this.scoreChain.score,
      });
    });
  }

  private pauseGame(shouldPause: boolean): void {
    if (this.completing || shouldPause === this.isPaused) return;
    this.isPaused = shouldPause;
    if (shouldPause) {
      this.physics.world.pause();
      this.inputRouter.clear();
      const container = this.add.container(240, 135).setDepth(1_000);
      const panel = this.add.rectangle(0, 0, 290, 92, 0x06111e, 0.96).setStrokeStyle(3, 0xd5a841);
      const title = this.add.text(0, -18, 'PAUZA', titleStyle(18, '#f2c85b')).setOrigin(0.5);
      const help = this.add.text(0, 22, 'ESC · wróć do gry   M · dźwięk', titleStyle(7, '#dbe8d3')).setOrigin(0.5);
      container.add([panel, title, help]);
      this.pauseOverlay = container;
    } else {
      this.pauseOverlay?.destroy();
      this.pauseOverlay = undefined;
      this.physics.world.resume();
    }
  }

  private toggleMute(): void {
    audio.setMuted(!audio.muted);
    this.store.setMuted(audio.muted);
    this.floatText(240, 45, audio.muted ? 'DŹWIĘK WYŁ.' : 'DŹWIĘK WŁ.', '#f6e5b6');
  }

  private updateHud(): void {
    this.hudScore.setText(`WYNIK ${this.scoreChain.score.toString().padStart(6, '0')}`);
    this.hudMultiplier.setText(this.scoreChain.multiplier > 1 ? `×${this.scoreChain.multiplier}` : '');
    const hearts = this.players.map((player, index) => {
      const icon = index === 0 ? 'K' : 'B';
      return `${icon}:${'♥'.repeat(player.hearts)}${'·'.repeat(3 - player.hearts)}`;
    }).join('  ');
    this.hudHearts.setText(hearts);
    if (this.boss && this.bossHud) {
      const health = `${'◆'.repeat(Math.max(0, this.boss.health))}${'◇'.repeat(Math.max(0, this.boss.maxHealth - this.boss.health))}`;
      const charge = this.boss.trapped
        ? 'W BAŃCE! DOTKNIJ JĄ'
        : `OSŁABIENIE ${'●'.repeat(this.boss.bubbleHits)}${'○'.repeat(BOSS_STAGGER_HITS - this.boss.bubbleHits)} · NASTĘPNA: ${this.boss.bubbleHits >= BOSS_STAGGER_HITS ? 'ŁAPIE' : 'TRAFIA'}`;
      this.bossHud.setText(`${this.boss.name.toUpperCase()}  ${health}\n${charge}`);
    }
  }

  private floatText(x: number, y: number, text: string, color: string): void {
    const label = this.add.text(x, y, text, titleStyle(7, color)).setOrigin(0.5).setDepth(700).setStroke('#06111e', 3);
    this.tweens.add({
      targets: label,
      y: y - 22,
      alpha: 0,
      duration: 720,
      ease: 'Cubic.Out',
      onComplete: () => label.destroy(),
    });
  }

  private sparkBurst(x: number, y: number, color: number, count = 8): void {
    for (let i = 0; i < count; i += 1) {
      const spark = this.add.image(x, y, 'spark').setTint(color).setDepth(30);
      const angle = (Math.PI * 2 * i) / count;
      const distance = 18 + (i % 3) * 5;
      this.tweens.add({
        targets: spark,
        x: x + Math.cos(angle) * distance,
        y: y + Math.sin(angle) * distance,
        alpha: 0,
        scale: 0.25,
        duration: 380 + (i % 3) * 60,
        onComplete: () => spark.destroy(),
      });
    }
  }
}
